import { Link } from "react-router-dom";
import { Phone, ArrowUpRight, ShieldCheck, Hammer, MapPin, Sparkles } from "lucide-react";
import SEO from "../components/SEO";
import CTAStrip from "../components/CTAStrip";
import { COMPANY, IMG } from "../data/site";
import { useLang } from "../i18n/LanguageContext";

const PRODUCT_TILES = [
    { to: "/portail-aluminium", titleKey: "tile.aluminium", tagKey: "tile.aluminium_tag", img: IMG.gateAlu, span: "md:col-span-7" },
    { to: "/portail-coulissant", titleKey: "tile.sliding", tagKey: "tile.sliding_tag", img: "https://customer-assets.emergentagent.com/job_leman-portails/artifacts/zt3xrlzj_portail.png", span: "md:col-span-5" },
    { to: "/clotures", titleKey: "tile.fences", tagKey: "tile.fences_tag", img: IMG.metalFence, span: "md:col-span-5" },
    { to: "/brise-vue", titleKey: "tile.privacy", tagKey: "tile.privacy_tag", img: IMG.briseVue, span: "md:col-span-7" },
    { to: "/contact", titleKey: "tile.install", tagKey: "tile.install_tag", img: IMG.workshop, span: "md:col-span-8", emphasis: true },
    { to: "/motorisation-portail", titleKey: "tile.motor", tagKey: "tile.motor_tag", img: IMG.motor, span: "md:col-span-4", small: true },
];

const VALUES = [
    { icon: Hammer, tKey: "val.workshop", dKey: "val.workshop_d" },
    { icon: ShieldCheck, tKey: "val.custom", dKey: "val.custom_d" },
    { icon: MapPin, tKey: "val.proximity", dKey: "val.proximity_d" },
    { icon: Sparkles, tKey: "val.aesthetics", dKey: "val.aesthetics_d" },
];

export default function Home() {
    const { t } = useLang();
    return (
        <>
            <SEO
                title="Portails, clôtures & brise-vue sur mesure autour du Léman"
                description="Portail du Léman installe portails, clôtures, brise-vue et motorisations sur mesure à Saint-Jean-de-Gonville, Genève, Pays de Gex et Haute-Savoie."
                keywords={["portail Genève", "portail Pays de Gex", "portail Haute-Savoie", "portail aluminium", "motorisation portail", "clôture Genève", "brise-vue Genève"]}
            />

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0" aria-hidden>
                    <img src={IMG.hero} alt="" className="w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0A1118]/85 via-[#0A1118]/75 to-[#0A1118]" />
                    <div className="absolute inset-0 grain opacity-60" />
                </div>

                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-20 md:pb-32">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                        <div className="lg:col-span-7">
                            <div className="divider-tag mb-6" data-testid="hero-tag">{t("hero.tag")}</div>
                            <h1 className="font-display text-[2.4rem] sm:text-5xl md:text-6xl lg:text-[4.4rem] text-white leading-[1.02] tracking-tight">
                                {t("hero.title_line1")}<br />
                                {t("hero.title_line2")}<br />
                                <span className="text-brand-accent italic">{t("hero.title_line3_a")}</span> {t("hero.title_line3_b")}
                            </h1>
                            <p className="mt-7 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
                                {t("hero.subtitle")}
                            </p>
                            <div className="mt-9 flex flex-col sm:flex-row gap-4" data-testid="hero-cta-row">
                                <Link to="/contact" data-testid="hero-quote-btn" className="btn-primary">
                                    {t("cta.quote")}
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                                <a href={`tel:${COMPANY.phones[0].tel}`} data-testid="hero-call-btn" className="btn-ghost">
                                    <Phone className="w-4 h-4" /> {t("cta.call")}
                                </a>
                                <Link to="/realisations" data-testid="hero-realisations-btn" className="btn-ghost">
                                    {t("cta.see_projects")}
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="relative group" data-testid="hero-logo-wrap">
                                <div className="absolute -inset-3 bg-gradient-to-br from-brand-accent/20 via-brand-primary/10 to-transparent blur-2xl opacity-70 group-hover:opacity-100 transition-opacity duration-700" aria-hidden />
                                <div className="relative aspect-square overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,194,199,0.35)] bg-black">
                                    <img
                                        src={COMPANY.logoHero || COMPANY.logo}
                                        alt="Portail du Léman — Portails, clôtures et brise-vue autour du Léman"
                                        className="w-full h-full object-contain transition-transform duration-[1200ms] group-hover:scale-[1.03]"
                                    />
                                    <div className="absolute inset-0 ring-1 ring-inset ring-white/[0.05] pointer-events-none" />
                                </div>
                                <div className="mt-5 hidden lg:flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-white/45">
                                    <span className="text-[11px] tracking-[0.28em] uppercase">{t("nav.gates")}</span>
                                    <span className="w-1 h-1 bg-brand-accent rounded-full" />
                                    <span className="text-[11px] tracking-[0.28em] uppercase">{t("nav.fences")}</span>
                                    <span className="w-1 h-1 bg-brand-accent rounded-full" />
                                    <span className="text-[11px] tracking-[0.28em] uppercase">{t("nav.privacy_screen")}</span>
                                    <span className="w-1 h-1 bg-brand-accent rounded-full" />
                                    <span className="text-brand-accent text-[12px] tracking-[0.22em] uppercase font-semibold">{t("hero.strip_installation")}</span>
                                    <span className="w-1 h-1 bg-brand-accent/60 rounded-full" />
                                    <span className="text-[9px] tracking-[0.32em] uppercase text-white/35">{t("hero.strip_motor")}</span>
                                </div>
                                <div className="mt-3 hidden lg:block text-center text-[11px] italic text-white/55">
                                    {t("hero.strip_sub")}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact card — moved below hero, full width on mobile, inline on lg */}
                    <div className="mt-14 lg:mt-20 grid lg:grid-cols-12 gap-6">
                        <div className="lg:col-span-7 hidden lg:block" />
                        <div className="lg:col-span-5">
                            <div className="border border-white/10 bg-white/[0.02] backdrop-blur-md p-6">
                                <div className="text-[10px] tracking-[0.28em] uppercase text-brand-accent/80 mb-4">{t("cta.contact_direct")}</div>
                                {COMPANY.phones.map((p) => (
                                    <a key={p.tel} href={`tel:${p.tel}`} className="flex items-center justify-between py-3 border-b border-white/[0.06] last:border-0 group">
                                        <span className="text-xs uppercase tracking-widest text-white/40">{p.label}</span>
                                        <span className="text-white group-hover:text-brand-accent transition flex items-center gap-2">
                                            <Phone className="w-4 h-4" /> {p.value}
                                        </span>
                                    </a>
                                ))}
                                <div className="mt-5 pt-5 border-t border-white/[0.08] text-xs text-white/55 leading-relaxed">
                                    {COMPANY.address.street}<br />
                                    {COMPANY.address.postalCode} {COMPANY.address.city}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Intro */}
            <section className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-start">
                <div className="md:col-span-5">
                    <div className="divider-tag mb-5">{t("about.tag")}</div>
                    <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05] tracking-tight">
                        {t("about.title_1")}<br />
                        <span className="italic text-white/80">{t("about.title_2")}</span>
                    </h2>
                </div>
                <div className="md:col-span-7 md:pt-10 text-white/70 leading-relaxed space-y-5 text-base md:text-[1.05rem]">
                    <p>{t("about.p1")}</p>
                    <p>{t("about.p2")}</p>
                    <p>{t("about.p3")}</p>
                    <p>{t("about.p4")}</p>
                    <p>{t("about.p5")}</p>
                    <p>{t("about.p6")}</p>
                </div>
            </section>

            {/* Values */}
            <section className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 md:pb-28">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.07] border border-white/[0.07]">
                    {VALUES.map(({ icon: Icon, tKey, dKey }, i) => (
                        <div key={tKey} className="bg-[#0B141C] p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="section-number">0{i + 1}</span>
                                <Icon className="w-5 h-5 text-brand-accent" />
                            </div>
                            <div className="font-display text-2xl text-white mb-2">{t(tKey)}</div>
                            <p className="text-sm text-white/55 leading-relaxed">{t(dKey)}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Products bento */}
            <section className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 md:pb-28">
                <div className="flex items-end justify-between mb-10 gap-6">
                    <div>
                        <div className="divider-tag mb-4">{t("section.skills_tag")}</div>
                        <h2 className="font-display text-4xl md:text-5xl text-white leading-tight tracking-tight">
                            {t("section.skills_title")}
                        </h2>
                    </div>
                    <Link to="/portails" className="hidden md:inline-flex btn-ghost">{t("cta.all")}</Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    {PRODUCT_TILES.map((tile) => (
                        <Link
                            key={tile.to + tile.titleKey}
                            to={tile.to}
                            data-testid={`tile-${tile.to.slice(1)}`}
                            className={`card-product group ${tile.span} relative block`}
                        >
                            <div className={`img-wrap overflow-hidden ${tile.small ? "aspect-[4/3]" : "aspect-[16/10] md:aspect-[16/11]"}`}>
                                <img src={tile.img} alt={t(tile.titleKey)} loading="lazy" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1118] via-[#0A1118]/30 to-transparent" />
                            </div>
                            <div className="absolute left-6 right-6 bottom-6 flex items-end justify-between">
                                <div>
                                    <div className={`tracking-[0.28em] uppercase text-brand-accent/90 mb-2 ${tile.small ? "text-[9px]" : "text-[10px]"}`}>{t(tile.tagKey)}</div>
                                    <div className={`font-display text-white ${tile.small ? "text-xl" : tile.emphasis ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"}`}>{t(tile.titleKey)}</div>
                                </div>
                                <ArrowUpRight className={`text-white opacity-70 group-hover:opacity-100 group-hover:text-brand-accent transition ${tile.small ? "w-5 h-5" : "w-6 h-6"}`} />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            <CTAStrip />

            {/* Zones quick */}
            <section className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="divider-tag mb-5">{t("section.zones_tag")}</div>
                        <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05] tracking-tight">
                            {t("section.zones_title_1")}<br />
                            <span className="italic text-white/80">{t("section.zones_title_2")}</span>
                        </h2>
                        <p className="mt-6 text-white/65 max-w-xl">
                            {t("section.zones_sub")}
                        </p>
                        <Link to="/zones-intervention" className="btn-ghost mt-7 inline-flex">{t("section.zones_btn")}</Link>
                    </div>
                    <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/[0.07] border border-white/[0.07]">
                        {COMPANY.areas.slice(0, 12).map((a) => (
                            <div key={a} className="bg-[#0B141C] py-5 px-5 text-sm text-white/70 hover:text-brand-accent transition cursor-default">
                                {a}
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
